import React from 'react';
import clsx from 'clsx';
import { clickTrackingLoc } from '../constants/ApplicationConstants';
import { useConditionalPageContextSERP, useConditionalServerDataHotelOverviewSERP } from '../../../hooks';
import { hardCodedAllLocale, KILOMETERSTOMILES, MILESTOKILOMETERS, MILESUNIT } from '../../../constants';
import { addSubDirectoryPrefix } from '../../../utils/src/helper';
import { getDistanceValueInKilometers, getDistanceValueInMiles } from '../../../utils/src/utils';
import { createDecimalValue } from '../../../modules/utils';
import { airportFeeDataTypes, feeDetailTypes } from './index.types';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Accordion = (airportInfo: any): any => {
  const accordionData = airportInfo?.aeroData;
  const { jsonContent } = useConditionalServerDataHotelOverviewSERP();
  const { currentLocale, pageVal } = useConditionalPageContextSERP();

  const airportPhoneNumber =
    accordionData?.contactNumber?.number ?? accordionData?.contactNumber?.phoneNumber?.original;

  const formatPhoneNumber = (phoneNumberString: string): string => {
    let returnedNumber = '';
    if (phoneNumberString?.includes('+')) {
      if (phoneNumberString.replaceAll(' ', '').replaceAll('-', '')?.split('')?.length > 12) {
        returnedNumber = phoneNumberString
          .replaceAll(' ', '')
          .replaceAll('-', '')
          .replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3');
      } else {
        returnedNumber = phoneNumberString
          .replaceAll(' ', '')
          .replaceAll('-', '')
          .replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1 $2-$3-$4');
      }
    }
    return returnedNumber;
  };

  // This logic is implemented to apply a New Logic for Airport Shuttle
  const getAirportAccordionLabel = (airportsData: airportFeeDataTypes) => {
    const shuttleFeeVal = airportsData?.fees?.find((item: feeDetailTypes) => item?.type?.code === 'shuttleFee');
    if (airportsData?.complimentaryShuttle) {
      return jsonContent?.['shuttleServiceLabel'];
    } else if (!airportsData?.complimentaryShuttle && !shuttleFeeVal) {
      return jsonContent?.['noShuttleServiceLabel'];
    } else if (!airportsData?.complimentaryShuttle && shuttleFeeVal) {
      const amountDetails = shuttleFeeVal?.details?.amount;
      const decimalPoint = Number(amountDetails?.decimalPoint);
      const displayAmount =
        decimalPoint > 0 ? createDecimalValue(Number(amountDetails?.amount), decimalPoint) : amountDetails?.amount;
      const label = jsonContent?.['shuttleFeeAvailableLabel'];
      if (!label?.includes('{X}')) return '';
      return label.replace('{X}', String(displayAmount)).replace('{currency}', amountDetails?.currency);
    } else {
      return null;
    }
  };

  const getFormattedDistance = () => {
    const description = accordionData?.distanceDetails?.description;
    const unitCode = accordionData?.distanceDetails?.unit?.code;
    const direction = description?.split(' ')?.[2] ?? '';

    if (currentLocale === hardCodedAllLocale?.en) {
      // EN-US + Miles => as-is
      if (unitCode === MILESUNIT) {
        return description;
      }

      // EN-US + KM => convert to Miles
      return `${getDistanceValueInMiles(description, currentLocale)}
      ${jsonContent?.milesUnitLabel || 'mi'}
      ${direction}`;
    }

    // Non-EN-US + Miles => convert to KM
    if (unitCode === MILESUNIT) {
      return `${getDistanceValueInKilometers(description, currentLocale)}
      ${jsonContent?.kmSearchLabel || jsonContent?.kmLabel || 'km'}
      ${direction}`;
    }
    // Non-EN-US + KM => as-is
    return description;
  };

  return (
    <div className="accordion-item">
      <h2 className="accordion-header location-box-header" id={accordionData.name}>
        <button
          tabIndex={0}
          className={clsx(
            'accordion-button',
            'location-box__accordian',
            'custom_click_track',
            airportInfo.active ? '' : 'show-border collapsed'
          )}
          type="button"
          data-bs-toggle="collapse"
          data-bs-target={`#${accordionData.name}${airportInfo.accIndex}`}
          aria-expanded={airportInfo.active ? 'true' : 'false'}
          aria-controls={`${accordionData.name}${airportInfo.accIndex}`}
          onClick={(e): void => {
            airportInfo.accordionClick(e, airportInfo.accIndex);
          }}
          onKeyDown={(e): void => {
            airportInfo.accordionClick(e, airportInfo.accIndex);
          }}
          {...{
            custom_click_track_value: `${clickTrackingLoc}| Airport Accordian: ${accordionData.name} |internal`,
          }}
        >
          <div className={clsx('m-accordion-title', 'airplane-icon-name', 'accordion-element')}>
            <span
              className={clsx(
                pageVal === 'chat' ? '' : 'icon-decorative',
                'icon-s',
                'mr-2',
                'icon-airplane',
                pageVal === 'chat' ? 'flex-shrink-0' : ''
              )}
            ></span>
            <span className={clsx(pageVal === 'chat' ? 't-font-s' : 't-font-m', 'location-box__location-name')}>
              {accordionData.name}
            </span>
            <span
              className={clsx(
                'accordion-arrow-icon',
                pageVal === 'chat' ? 'icon-l' : '',
                airportInfo.active ? 'ml-3 icon-dropdown-up' : 'ml-3 icon-dropdown-down'
              )}
            ></span>
          </div>
        </button>
      </h2>
      {airportInfo.active && (
        <div
          id={`${accordionData.name}${airportInfo.accIndex}`}
          className={clsx(
            'accordion-collapse',
            'collapse panel',
            'accordion-content',
            airportInfo.active ? ' show' : ''
          )}
          aria-label={accordionData.name}
          role="region"
          data-bs-parent="#accordionExample1"
        >
          <p className="accordion-body">
            <p className="t-font-s">
              {jsonContent?.['hotelDirectionLabel'] ?? jsonContent?.['hotelDirectionLabel_shop_spa']}: :{' '}
              {getFormattedDistance()}
            </p>
            {airportPhoneNumber && (
              <a href={`tel:` + airportPhoneNumber} className="t-font-s">
                <span>
                  {jsonContent?.['airportPhoneLabel']}:&nbsp;
                  <span dir="ltr" className="phone-number">
                    {formatPhoneNumber(airportPhoneNumber)}
                  </span>
                </span>
              </a>
            )}
            <p> </p>
            <a
              href={addSubDirectoryPrefix(accordionData.url)}
              className={clsx('hotel-website', 'custom_click_track')}
              target="_blank"
              rel="noreferrer"
              {...{ custom_click_track_value: `${clickTrackingLoc}| ${accordionData.name} website link |internal` }}
            >
              <span className="website-link">
                <p className={clsx('m-link-tertiary-button-external', 'underline-content')}>
                  {jsonContent?.['visitLabel']} {accordionData.id} {jsonContent?.['airportWebsiteLabel']}
                </p>
              </span>
              <span aria-label="Opens in a new browser window." role="region"></span>
            </a>
            {getAirportAccordionLabel(accordionData) && (
              <p className="t-font-s">{getAirportAccordionLabel(accordionData)}</p>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default Accordion;
